// WP-6.5 (M29 AD-5, LOCKED §2.36, S-32, CA-5): the Why?-expander content — "every recommendation
// shows reasoning, assumptions, key inputs, confidence, and trade-offs", S-32's own verbatim
// structure (inputs → assumptions → confidence → trade-offs). TEMPLATE-rendered from response
// data ALREADY on hand (CA-5: "template-rendered from engine output by default; LLM wording is
// reserved for free-form Q&A") — no new engine/API call, no LLM, just plain string composition
// over what the Suggestion/Affordability response already carries.
import { formatMoney, fromMinor } from '@pfos/engine';
import type { AffordabilityResponse, SuggestionResponse } from '@pfos/shared';

export interface WhySections {
  inputs: string[];
  assumptions: string[];
  confidence: string;
  tradeOffs: string[];
}

const DETERMINISTIC_CONFIDENCE =
  'High — a deterministic calculation from your own recorded accounts, bills, and goals, not an estimate.';

export function buildSuggestionWhy(suggestion: SuggestionResponse): WhySections {
  const money = (minorUnits: number): string =>
    formatMoney(fromMinor(suggestion.currency, minorUnits));
  return {
    inputs: [
      `New income: ${money(suggestion.incomeMinor)}`,
      `As of: ${new Date(suggestion.asOf).toLocaleDateString()}`,
      ...suggestion.lines.map(
        (line) => `${line.tier} — ${line.label}: ${money(line.allocatedMinor)}`,
      ),
    ],
    assumptions: [
      'Tiers are funded strictly in priority order: overdue commitments (P1), then your safety-reserve floor (P2), then essential bills due in the next 30 days (P3), then your other goals in priority order (P4) — nothing reaches a later tier until the one before it is fully funded.',
      'A strict goal is funded only through the safety-reserve tier (P2), never counted again under your other goals (P4).',
      "A bill only counts as essential if you've flagged its category essential in Settings.",
    ],
    confidence: DETERMINISTIC_CONFIDENCE,
    tradeOffs: [
      'Accepting sets this money aside as a reserve — fully visible and releasable any time, but it stops counting toward Spendable Now until you release it.',
      'Any amount left as Discretionary is not earmarked for anything and stays fully free to spend.',
    ],
  };
}

export function buildAffordabilityWhy(answer: AffordabilityResponse): WhySections {
  const money = (minorUnits: number): string => formatMoney(fromMinor(answer.currency, minorUnits));
  return {
    inputs: [
      `Amount: ${money(answer.amountMinor)} for ${answer.purpose}`,
      `Spendable now: ${money(answer.spendableNowMinor)}`,
      `Already spoken for: ${money(answer.protectedFloorMinor)}`,
      `Confirmed incoming: ${money(answer.upcomingInflowMinor)}`,
    ],
    assumptions: [
      '"Already spoken for" combines any overdue commitments, your safety-reserve shortfall, and essential bills due in the next 30 days.',
      '"Confirmed incoming" counts only open receivables and upcoming recurring income — never speculative or hoped-for money.',
    ],
    confidence: DETERMINISTIC_CONFIDENCE,
    tradeOffs: [
      answer.futureImpactMinor < 0
        ? `Paying this would leave you ${money(-answer.futureImpactMinor)} below what's already committed.`
        : `You'd still have ${money(answer.futureImpactMinor)} of headroom above what's already committed.`,
      answer.waitingWouldHelp
        ? 'Waiting for the confirmed incoming money above would close the gap.'
        : 'Waiting would not currently change the outcome — nothing pending closes the gap.',
    ],
  };
}
